import { createElement, useState, useRef, useMemo, ReactNode } from 'react';
import { Popover } from '../Popover/Popover';
import { Calendar } from '../Calendar';
import { DatePickerPopoverProps } from './';
import { isSameDay, getMonthAndYearByDate } from './DateUtil';
import { TimePicker } from './Pickers/TimePicker';
import { WeekPicker } from './Pickers/WeekPicker';
import { MonthPicker } from './Pickers/MonthPicker';
import { QuarterPicker } from './Pickers/QuarterPicker';
import { Button } from '../Button';
import { MessageDialog, useMessageDialogText } from '../MessageDialog';
import { Flow } from '../Layouts/Flow';
import { PickerRef } from './Pickers/PickerRef';
import { isPastDate, isAfterDate } from '../Calendar/DisabledDayFilters';
import { useDefaultDateTimeFormat } from './useDefaultDateTimeFormat';
import { postfixTestId } from '../../util/testId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting/useFormatLocale';
import bundle from './NovaComponents-DatePicker-gui-icu.properties';

import './DatePicker.css';
import cn from './DatePicker.css.json';

const useTextLocalizer = createUseTextLocalizer(bundle);

function hasTimePickerProps(x: {
  type?: DatePickerPopoverProps['type'];
}): x is { type: 'time' | 'datetime' } {
  return x.type === 'time' || x.type === 'datetime';
}

export const DatePickerPopover = (props: DatePickerPopoverProps) => {
  const localize = useTextLocalizer();
  const locale = useFormatLocale();
  const [hideMessageDialog, setHideMessageDialog] = useState(true);
  const { CLOSE } = useMessageDialogText();
  const refPicker = useRef<PickerRef>(null);

  const defaultMinDate = useMemo<Date>(() => new Date(1582, 0, 1), []);
  const defaultMaxDate = useMemo<Date>(() => new Date(9999, 11, 31), []);

  const defaultFormat = useDefaultDateTimeFormat({
    type: props.type ?? 'date',
    showMilliseconds: hasTimePickerProps(props) && props.showMilliseconds,
  });

  const minDate = useMemo<Date>(() => {
    if (
      props.minDate &&
      isAfterDate(props.minDate, { today: defaultMinDate }) &&
      (isPastDate(props.minDate, { today: defaultMaxDate }) ||
        isSameDay(props.minDate, defaultMaxDate))
    ) {
      const mnDate = new Date(props.minDate);
      if (props.type === 'datetime' && !props.showMilliseconds) mnDate.setMilliseconds(0);
      if (props.type === 'month') {
        // Set to 1st day of month
        mnDate.setDate(1);
        mnDate.setHours(0);
        mnDate.setMinutes(0);
        mnDate.setSeconds(0);
      } else if (props.type === 'quarter') {
        // Set minDate to beginning of the quarter
        mnDate.setDate(1);
        if (mnDate.getMonth() >= 0 && mnDate.getMonth() < 3) {
          mnDate.setMonth(0);
        } else if (mnDate.getMonth() >= 3 && mnDate.getMonth() < 6) {
          mnDate.setMonth(3);
        } else if (mnDate.getMonth() >= 6 && mnDate.getMonth() < 9) {
          mnDate.setMonth(6);
        } else if (mnDate.getMonth() >= 9) {
          mnDate.setMonth(9);
        }
        mnDate.setHours(0);
        mnDate.setMinutes(0);
        mnDate.setSeconds(0);
      }
      return mnDate;
    } else return new Date(defaultMinDate);
  }, [defaultMaxDate, defaultMinDate, props.minDate, props.showMilliseconds, props.type]);

  const maxDate = useMemo<Date>(() => {
    if (
      props.maxDate &&
      (isAfterDate(props.maxDate, { today: defaultMinDate }) ||
        isSameDay(props.maxDate, defaultMinDate)) &&
      isPastDate(props.maxDate, { today: defaultMaxDate })
    ) {
      const mxDate = new Date(props.maxDate);
      if (props.type === 'datetime' && !props.showMilliseconds) mxDate.setMilliseconds(0);
      if (props.type === 'month') {
        // Set to last day of month
        mxDate.setDate(new Date(mxDate.getFullYear(), mxDate.getMonth() + 1, 0).getDate());
        mxDate.setHours(23);
        mxDate.setMinutes(59);
        mxDate.setSeconds(59);
      } else if (props.type === 'quarter') {
        // Set maxDate to end of the quarter
        mxDate.setDate(1);
        if (mxDate.getMonth() >= 0 && mxDate.getMonth() < 3) {
          mxDate.setMonth(2);
        } else if (mxDate.getMonth() >= 3 && mxDate.getMonth() < 6) {
          mxDate.setMonth(5);
        } else if (mxDate.getMonth() >= 6 && mxDate.getMonth() < 9) {
          mxDate.setMonth(8);
        } else if (mxDate.getMonth() >= 9) {
          mxDate.setMonth(11);
        }
        mxDate.setDate(new Date(mxDate.getFullYear(), mxDate.getMonth() + 1, 0).getDate());
        mxDate.setHours(23);
        mxDate.setMinutes(59);
        mxDate.setSeconds(59);
      }
      return mxDate;
    } else return new Date(defaultMaxDate);
  }, [defaultMaxDate, defaultMinDate, props.maxDate, props.showMilliseconds, props.type]);

  const isValidDateTime = (day: Date) => {
    if (props.type !== 'datetime') return true;
    const testDate = new Date(day);
    if (!props.showMilliseconds) testDate.setMilliseconds(0);
    if (testDate.getTime() < minDate.getTime() || testDate.getTime() > maxDate.getTime())
      return false;
    return true;
  };

  const state = props.value ?? new Date();
  const showHours = props.showHours ?? true;
  const showSeconds = props.showSeconds ?? true;
  const showDayPeriod = props.showDayPeriod ?? true;

  let picker: ReactNode;
  let pickerAriaLabel = localize('date.dialog.aria');
  switch (props.type) {
    case null:
    case undefined:
    case 'date':
      pickerAriaLabel = localize('date.dialog.aria');
      picker = (
        <Calendar
          {...props}
          value={state}
          onChange={(e) => {
            props.onChange?.({ value: e.value ?? new Date() });
          }}
          ref={refPicker}
        />
      );
      break;
    case 'datetime':
      pickerAriaLabel = localize('datetime.dialog.aria');
      picker = (
        <Flow orientation="vertical" className={cn.dateTimeContainer}>
          <Calendar
            {...props}
            value={state}
            onChange={(e) => {
              const nextValue = new Date(e.value ?? new Date());
              nextValue.setHours(state.getHours());
              nextValue.setMinutes(state.getMinutes());
              nextValue.setSeconds(state.getSeconds());
              nextValue.setMilliseconds(state.getMilliseconds());
              if (!props.showMilliseconds) nextValue.setMilliseconds(0);
              props.onChange?.({ value: nextValue });
            }}
            ref={refPicker}
          />
          <TimePicker
            testId={props.testId}
            ref={refPicker}
            value={state}
            onChange={(e) => {
              props.onChange?.({ value: e });
            }}
            showMilliseconds={!!props.showMilliseconds}
            showHours={showHours}
            showSeconds={showSeconds}
            showDayPeriod={showDayPeriod}
          />
        </Flow>
      );
      break;
    case 'month':
      pickerAriaLabel = localize('month.dialog.aria');
      picker = (
        <MonthPicker
          testId={props.testId}
          value={state}
          onChange={(e) => {
            props.onChange?.({ value: e });
          }}
          ref={refPicker}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
      break;
    case 'quarter':
      pickerAriaLabel = localize('quarter.dialog.aria');
      picker = (
        <QuarterPicker
          testId={props.testId}
          value={state}
          onChange={(e) => {
            props.onChange?.({ value: e });
          }}
          ref={refPicker}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
      break;
    case 'week':
      pickerAriaLabel = localize('week.dialog.aria');
      picker = (
        <WeekPicker
          testId={props.testId}
          value={state}
          onChange={(e) => {
            props.onChange?.({ value: e });
          }}
          ref={refPicker}
          minDate={minDate}
          maxDate={maxDate}
        />
      );
      break;
    case 'time':
      pickerAriaLabel = localize('time.dialog.aria');
      picker = (
        <TimePicker
          testId={props.testId}
          ref={refPicker}
          value={state}
          onChange={(e) => {
            props.onChange?.({ value: e });
          }}
          showMilliseconds={!!props.showMilliseconds}
          showHours={showHours}
          showSeconds={showSeconds}
          showDayPeriod={showDayPeriod}
          showHoursDuration={!!props.showHoursDuration}
          showMinutesDuration={!!props.showMinutesDuration}
        />
      );
      break;
  }

  const printMinMaxErrorMessage = useMemo(() => {
    const minMaxMonthErrorMsg = localize('minmax.month.error.msg', {
      min: getMonthAndYearByDate(locale, minDate),
      max: getMonthAndYearByDate(locale, maxDate),
    });

    const minMaxQuarterErrorMsg = localize('minmax.quarter.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate),
    });

    const minMaxDateErrorMsg = localize('minmax.date.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate),
    });

    const minMaxDateTimeErrorMsg = localize('minmax.datetime.error.msg', {
      min: (props.format ?? defaultFormat)(minDate),
      max: (props.format ?? defaultFormat)(maxDate),
    });
    switch (props.type) {
      case 'month':
        return minMaxMonthErrorMsg;
      case 'quarter':
        return minMaxQuarterErrorMsg;
      case 'datetime':
        return minMaxDateTimeErrorMsg;
      default:
        return minMaxDateErrorMsg;
    }
  }, [defaultFormat, locale, localize, maxDate, minDate, props.format, props.type]);

  return (
    <Popover
      referenceElementRef={props.referenceElementRef}
      isOpen={props.isOpen}
      placement="auto"
      testId={postfixTestId(props.testId, 'popper')}
      isModal
      onEscape={() => {
        props.onEscape?.();
      }}
      ariaLabel={pickerAriaLabel}
      role="dialog"
      returnFocusOnEscape={false}
      footer={
        <div className={cn.footer}>
          <Button
            text={localize('ok.button.label')}
            testId="datePickerPopperOkButton"
            onPress={() => {
              if (refPicker.current && !refPicker.current.isValid()) {
                refPicker.current.focus();
              } else if (!isValidDateTime(state)) {
                setHideMessageDialog(false);
              } else {
                props.onOkPress?.();
              }
            }}
          />
          <MessageDialog
            testId="datePickerErrorMessageDialog"
            type="error"
            isOpen={!hideMessageDialog}
            title={localize('datetime.messagedialog.title')}
            text={printMinMaxErrorMessage}
            dismissAction={{
              text: CLOSE,
              onPress: () => {
                setHideMessageDialog(true);
              },
              isDefault: true,
            }}
          />
          <Button
            text={localize('cancel.button.label')}
            testId="datePickerPopperCancelButton"
            onPress={() => {
              props.onCancelPress?.();
            }}
          />
        </div>
      }
    >
      {picker}
    </Popover>
  );
};
